import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Sparkles, Send, Plus, Trash2, Bot, User, Loader2, MessageSquare, Coins, AlertCircle } from 'lucide-react';
import { API } from '@/App';

const MemberCopilotPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [balance, setBalance] = useState(null);
  const [noCredits, setNoCredits] = useState(false);
  const initialSentRef = useRef(false);
  const messagesEnd = useRef(null);
  const inputRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'super_admin';

  const SUGGESTIONS = [
    "Quels sont les types de lentilles de contact disponibles ?",
    "Comment se déroule une adaptation en lentilles rigides ?",
    "Quelles sont les contre-indications au port de lentilles ?",
  ];

  useEffect(() => {
    loadSessions();
    loadBalance();
  }, []);

  useEffect(() => {
    const msg = location.state?.initialMessage;
    if (msg && !initialSentRef.current) {
      initialSentRef.current = true;
      navigate(location.pathname, { replace: true, state: null });
      sendMessage(msg);
    }
  }, [location.state]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  const loadBalance = async () => {
    try {
      const res = await axios.get(`${API}/credits/balance`, { headers });
      setBalance(res.data);
      setNoCredits(!isAdmin && res.data.credits <= 0);
    } catch { }
  };

  const loadSessions = async () => {
    try {
      const res = await axios.get(`${API}/copilot/sessions`, { headers });
      setSessions(res.data);
    } catch { }
  };

  const loadSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/copilot/sessions/${sessionId}`, { headers });
      setActiveSession(sessionId);
      setMessages(res.data.messages || []);
      setNoCredits(false);
    } catch { }
  };

  const newChat = () => {
    setActiveSession(null);
    setMessages([]);
    setInput('');
    setNoCredits(!isAdmin && (balance?.credits ?? 0) <= 0);
    inputRef.current?.focus();
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/copilot/sessions/${sessionId}`, { headers });
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (activeSession === sessionId) newChat();
    } catch { }
  };

  const sendMessage = async (text) => {
    if (!text?.trim() || streaming) return;
    const userMsg = text.trim();
    setInput('');

    const sessionId = activeSession || crypto.randomUUID();
    if (!activeSession) setActiveSession(sessionId);

    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setStreaming(true);
    setStreamingText('');

    try {
      const response = await fetch(`${API}/copilot/chat`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg, session_id: sessionId }),
      });

      if (response.status === 402) {
        setNoCredits(true);
        setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ Crédits IA insuffisants. Rechargez votre solde pour continuer.' }]);
        setStreaming(false);
        loadBalance();
        return;
      }

      if (!response.ok) {
        setMessages(prev => [...prev, { role: 'assistant', content: `Erreur : ${response.status}` }]);
        setStreaming(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'delta') {
                fullText += data.content;
                setStreamingText(fullText);
              } else if (data.type === 'done') {
                setMessages(prev => [...prev, { role: 'assistant', content: fullText }]);
                setStreamingText('');
              } else if (data.type === 'error') {
                setMessages(prev => [...prev, { role: 'assistant', content: `Erreur : ${data.content}` }]);
                setStreamingText('');
              }
            } catch { }
          }
        }
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Erreur de connexion : ${err.message}` }]);
      setStreamingText('');
    }

    setStreaming(false);
    loadSessions();
    loadBalance();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="member-copilot-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles size={20} className="text-[#059669]" /> Copilote IA
          </h1>
          <p className="text-sm text-gray-500">Votre assistant personnel SFOALC</p>
        </div>
        {/* Credits badge */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg" data-testid="credits-badge">
            <Coins size={14} className={isAdmin || (balance?.credits > 0) ? 'text-[#059669]' : 'text-gray-400'} />
            <span className="text-sm font-bold text-gray-900">{isAdmin ? 'Illimité' : (balance?.credits ?? '...')}</span>
            {!isAdmin && <span className="text-xs text-gray-500">crédits</span>}
          </div>
          {!isAdmin && (
            <button onClick={() => navigate('/credits')}
              className="text-xs font-medium text-[#35AFE4] hover:text-[#1e3a5f] transition-colors"
              data-testid="buy-credits-link">
              Recharger
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 220px)', minHeight: '400px' }}>
        <div className="flex h-full">
          {/* Sessions sidebar */}
          <div className="hidden lg:flex flex-col w-56 border-r border-gray-100">
            <div className="p-3 border-b border-gray-100">
              <button onClick={newChat}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#059669] text-white text-xs font-medium hover:bg-[#047857] transition-colors"
                data-testid="new-chat-btn">
                <Plus size={14} /> Nouvelle conversation
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {sessions.map(s => (
                <button
                  key={s.session_id}
                  onClick={() => loadSession(s.session_id)}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 mb-1 rounded-lg text-left transition-colors group ${
                    activeSession === s.session_id ? 'bg-[#059669]/10 text-[#059669]' : 'text-gray-600 hover:bg-gray-50'
                  }`}>
                  <MessageSquare size={12} className="flex-shrink-0" />
                  <span className="text-[11px] truncate flex-1">{s.preview || 'Conversation'}</span>
                  <button onClick={(e) => deleteSession(s.session_id, e)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 hover:text-red-500 transition-all">
                    <Trash2 size={10} />
                  </button>
                </button>
              ))}
            </div>
          </div>

          {/* Chat area */}
          <div className="flex-1 flex flex-col">
            {messages.length === 0 && !streamingText ? (
              /* Welcome screen */
              <div className="flex-1 flex flex-col items-center justify-center p-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#059669] to-[#047857] flex items-center justify-center mb-4 shadow-lg">
                  <Sparkles size={24} className="text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-1">Assistant IA SFOALC</h2>
                <p className="text-sm text-gray-500 mb-5 text-center max-w-md">
                  Posez vos questions sur la contactologie, les lentilles et l'ophtalmologie. Chaque message utilise 1 crédit.
                </p>
                {noCredits && (
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 max-w-md" data-testid="no-credits-warning">
                    <AlertCircle size={16} className="text-amber-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-amber-900">Crédits insuffisants</p>
                      <button onClick={() => navigate('/credits')} className="text-xs text-amber-700 underline">Recharger mes crédits</button>
                    </div>
                  </div>
                )}
                <div className="grid gap-2 w-full max-w-md">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(s)}
                      disabled={noCredits && !isAdmin}
                      className="text-left px-4 py-3 rounded-xl border border-gray-200 hover:border-[#059669] hover:bg-[#059669]/5 text-sm text-gray-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`suggestion-${i}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Messages */
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-lg bg-[#059669] flex items-center justify-center flex-shrink-0">
                        <Bot size={14} className="text-white" />
                      </div>
                    )}
                    <div className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm ${
                      msg.role === 'user'
                        ? 'bg-[#1e3a5f] text-white rounded-br-md'
                        : 'bg-gray-100 text-gray-900 rounded-bl-md prose prose-sm max-w-none'
                    }`}>
                      {msg.role === 'assistant' ? <ReactMarkdown>{msg.content}</ReactMarkdown> : msg.content}
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-7 h-7 rounded-lg bg-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                        <User size={14} className="text-white" />
                      </div>
                    )}
                  </div>
                ))}
                {streamingText && (
                  <div className="flex gap-3">
                    <div className="w-7 h-7 rounded-lg bg-[#059669] flex items-center justify-center flex-shrink-0">
                      <Bot size={14} className="text-white" />
                    </div>
                    <div className="max-w-[75%] rounded-2xl rounded-bl-md bg-gray-100 text-gray-900 px-4 py-3 text-sm prose prose-sm max-w-none">
                      <ReactMarkdown>{streamingText}</ReactMarkdown>
                      <span className="inline-block w-1 h-4 bg-[#059669] animate-pulse ml-0.5" />
                    </div>
                  </div>
                )}
                <div ref={messagesEnd} />
              </div>
            )}

            {/* No credits banner */}
            {noCredits && messages.length > 0 && (
              <div className="bg-amber-50 border-t border-amber-200 px-4 py-2.5 flex items-center justify-between" data-testid="credits-depleted-bar">
                <span className="text-xs text-amber-700 font-medium">Crédits épuisés — rechargez pour continuer</span>
                <button onClick={() => navigate('/credits')}
                  className="px-3 py-1 bg-amber-500 text-white rounded-lg text-xs font-bold hover:bg-amber-600 transition-colors">
                  Recharger
                </button>
              </div>
            )}

            {/* Input */}
            <div className="border-t border-gray-100 p-3 bg-white">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={noCredits && !isAdmin ? "Rechargez vos crédits pour continuer..." : "Posez votre question..."}
                  rows={1}
                  className="flex-1 resize-none rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#059669]/30 focus:border-[#059669] disabled:bg-gray-50"
                  data-testid="copilot-input"
                  disabled={streaming || (noCredits && !isAdmin)}
                />
                <button
                  onClick={() => sendMessage(input)}
                  disabled={!input.trim() || streaming || (noCredits && !isAdmin)}
                  className="p-2.5 rounded-xl bg-[#059669] text-white hover:bg-[#047857] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="copilot-send-btn">
                  {streaming ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </button>
              </div>
              {!isAdmin && (
                <p className="text-[10px] text-gray-400 mt-1.5 text-center">1 crédit par message · {balance?.credits ?? 0} crédit{(balance?.credits || 0) !== 1 ? 's' : ''} restant{(balance?.credits || 0) !== 1 ? 's' : ''}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberCopilotPage;
